import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useNavigate } from "react-router-dom";

import { tokenStore } from "../api/client";
import { fetchMe, login as apiLogin, logout as apiLogout } from "../api/auth";
import type { MeResponse } from "../api/schemas";

type Status = "loading" | "authenticated" | "unauthenticated";

interface AuthContextValue {
  status: Status;
  me: MeResponse | null;
  signIn(username: string, password: string): Promise<{ mustChange: boolean }>;
  signOut(): Promise<void>;
  refreshMe(): Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<MeResponse | null>(null);
  const [status, setStatus] = useState<Status>("loading");
  const navigate = useNavigate();

  /** Pull /me on mount if we have a stored token. */
  useEffect(() => {
    let cancelled = false;
    async function boot() {
      if (!tokenStore.access) {
        setStatus("unauthenticated");
        return;
      }
      try {
        const data = await fetchMe();
        if (cancelled) return;
        setMe(data);
        setStatus("authenticated");
      } catch {
        if (cancelled) return;
        tokenStore.clear();
        setStatus("unauthenticated");
      }
    }
    void boot();
    return () => {
      cancelled = true;
    };
  }, []);

  /** Listen for the interceptor's "session expired" signal. */
  useEffect(() => {
    function handler() {
      setMe(null);
      setStatus("unauthenticated");
      navigate("/login", { replace: true });
    }
    window.addEventListener("np:auth:expired", handler);
    return () => window.removeEventListener("np:auth:expired", handler);
  }, [navigate]);

  const signIn = useCallback(
    async (username: string, password: string) => {
      const pair = await apiLogin({ username, password });
      const data = await fetchMe();
      setMe(data);
      setStatus("authenticated");
      return { mustChange: pair.must_change_password };
    },
    []
  );

  const signOut = useCallback(async () => {
    await apiLogout();
    setMe(null);
    setStatus("unauthenticated");
    navigate("/login", { replace: true });
  }, [navigate]);

  const refreshMe = useCallback(async () => {
    const data = await fetchMe();
    setMe(data);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ status, me, signIn, signOut, refreshMe }),
    [status, me, signIn, signOut, refreshMe]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
